import { useState, useMemo, useEffect } from 'react'
import { useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { CircularProgressModal, ConfirmPopupModal } from '@!/components/Modal'
import {
  newCardTokenAtom,
  cardInfoAtom,
  newCardInfoAtom,
  purchaseTypeAtom,
  listIndexAtom,
  monthlyCourseIdAtom,
  apiTimeAtom,
  initPaymentAtom,
  payTypeAtom,
  payStartUrlAtom,
} from '@!/atoms/paymentAtom'
import { usePaymentSaveCard, usePaymentExecPurchase, useMonthlyCourse } from '@!/Hooks'
import { isBlank } from '@!/Util'
import { Box } from '@chakra-ui/react'
import { PageBackLink, PaymentForm } from '@!/User/Payment/components'
import { usePaymentValidation } from '@!/User/Payment/Hooks/usePaymentValidation'

export const PaymentConfirm = () => {
  usePaymentValidation()

  const navigate = useNavigate()
  const [purchaseType] = useAtom(purchaseTypeAtom)
  const [listIndex] = useAtom(listIndexAtom)
  const [monthlyCourseId] = useAtom(monthlyCourseIdAtom)
  const [cardInfo] = useAtom(cardInfoAtom)
  const [newCardToken] = useAtom(newCardTokenAtom)
  const [newCardInfo] = useAtom(newCardInfoAtom)
  const [apiTime] = useAtom(apiTimeAtom)
  const [, initPayment] = useAtom(initPaymentAtom)
  const [, setPayType] = useAtom(payTypeAtom)
  const [, setPayStartUrl] = useAtom(payStartUrlAtom)
  const [isProcessing, setIsProcessing] = useState(false)
  const initApiError = { error: null, errorInfo: null }
  const [apiError, setApiError] = useState(initApiError)

  const [saveCardMutation, saveCardApiError] = usePaymentSaveCard()
  const [execPurchaseMutation, execPurchaseApiError] = usePaymentExecPurchase()
  const { registerMonthlyCourseMutation, apiError: registerMonthlyCourseApiError } = useMonthlyCourse()

  // save_card APIの実行結果後処理
  useEffect(() => {
    if (!saveCardMutation.isSuccess) return

    setApiError(initApiError)
    setPayType('credit')

    if (purchaseType === 'coin') {
      execPurchaseMutation.mutate({ listIndex, apiTime })
    }

    if (purchaseType === 'monthly') {
      registerMonthlyCourseMutation.mutate({ monthly_course_id: monthlyCourseId })
    }
  }, [saveCardMutation.isSuccess])

  useEffect(() => {
    if (!saveCardMutation.isError) return

    setApiError(saveCardApiError)
    setIsProcessing(false)
  }, [saveCardMutation.isError, saveCardApiError])

  // exec_purchase APIの実行結果後処理
  useEffect(() => {
    if (!execPurchaseMutation.isSuccess) return

    setApiError(initApiError)
    setIsProcessing(false)

    if (isBlank(execPurchaseMutation.data.redirect_url)) {
      const { order_id } = execPurchaseMutation.data
      const params = new URLSearchParams({ order_id, cancel: false, error: false })
      window.location.href = `/static_page/payment_complete?${params.toString()}`
    } else {
      setPayStartUrl(execPurchaseMutation.data.redirect_url)
      navigate('/redirect')
    }
  }, [execPurchaseMutation.isSuccess])

  useEffect(() => {
    if (!execPurchaseMutation.isError) return

    setApiError(execPurchaseApiError)
    setIsProcessing(false)
  }, [execPurchaseMutation.isError, execPurchaseApiError])

  // 月額コース登録APIの実行結果後処理
  useEffect(() => {
    if (!registerMonthlyCourseMutation.isSuccess) return

    setApiError(initApiError)
    setIsProcessing(false)

    if (isBlank(registerMonthlyCourseMutation.data.redirect_url)) {
      const { monthly_course_id, order_id, restore } = registerMonthlyCourseMutation.data
      const params = new URLSearchParams({ monthly_course_id, order_id, restore })
      window.location.href = `/static_page/monthly_course_registration_complete?${params.toString()}`
    } else {
      setPayStartUrl(registerMonthlyCourseMutation.data.redirect_url)
      navigate('/redirect')
    }
  }, [registerMonthlyCourseMutation.isSuccess])

  useEffect(() => {
    if (!registerMonthlyCourseMutation.isError) return

    setApiError(registerMonthlyCourseApiError)
    setIsProcessing(false)
  }, [registerMonthlyCourseMutation.isError, registerMonthlyCourseApiError])

  const handleSubmit = () => {
    if (isProcessing) return

    setIsProcessing(true)
    setPayType('credit')

    if (newCardToken) {
      const update = !isBlank(cardInfo) ? true : false
      saveCardMutation.mutate({ token: newCardToken, isUpdate: update })
      return
    }

    if (purchaseType === 'coin') {
      execPurchaseMutation.mutate({ listIndex, apiTime })
    }

    if (purchaseType === 'monthly') {
      registerMonthlyCourseMutation.mutate({ monthly_course_id: monthlyCourseId })
    }
  }

  const goPurchaseTop = () => {
    initPayment()
  }

  const handleChangeCard = () => {
    navigate('/new_card')
  }

  const pageBack = (e) => {
    e.preventDefault()
    navigate(-1)
  }

  const apiErrorText = useMemo(() => {
    if (apiError.error === null) return ''

    if (apiError.error === 'fraudulent_registration') {
      return '登録回数が上限に達しました。時間をおいて再度お試しください。'
    } else if (apiError.error.indexOf('card_info') != -1) {
      const errorCode = apiError.errorInfo ? `(ErrorCode:${apiError.errorInfo})` : ''
      return '決済に失敗しました。入力内容をご確認ください' + errorCode
    } else if (apiError.error.indexOf('token_expire') != -1) {
      return '決済に失敗しました。再度入力をお願いします。'
    } else if (apiError.error.indexOf('coin_add') != -1) {
      return 'コイン付与に失敗しました。'
    } else if (apiError.error === 'FAILED_REGISTER') {
      return '月額コースの登録に失敗しました。再度登録を行ってください。'
    } else if (apiError.error === 'REGISTERED') {
      return 'すでに月額コースに登録済みです。'
    } else if (apiError.error === 'CANCEL_RESERVED') {
      return '月額コースの解除処理中です。処理が完了するまで再登録できません。'
    }
    return 'エラーが発生しました。しばらく時間をおいて再度お試しください。'
  }, [apiError])

  const handleApiErrorPopup = () => {
    if (apiError.error === null) return

    if (apiError.error.indexOf('token_expire') != -1) {
      goPurchaseTop()
      return
    } else if (apiError.error.indexOf('coin_add') != -1) {
      goPurchaseTop()
      return
    }

    setApiError(initApiError)
  }

  const currentCard = newCardToken ? newCardInfo : cardInfo

  return (
    <Box maxW="600px" m="auto">
      <PaymentForm newCardToken={newCardToken} currentCard={currentCard} handleSubmit={handleSubmit} onChangeCard={handleChangeCard} />
      <PageBackLink pageBack={pageBack} />
      <CircularProgressModal isOpen={isProcessing} />
      <ConfirmPopupModal handlePopup={handleApiErrorPopup} text={apiErrorText} isOpen={!!apiError.error} />
    </Box>
  )
}

export default PaymentConfirm
